import {
	BodyScrollEndEvent,
	CellClickedEvent,
	CellValueChangedEvent,
	FirstDataRenderedEvent,
	ICellRendererParams,
	ValueGetterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { MouseEvent, useCallback, useMemo, useRef } from "react";
import {
	aggridHeaderClass,
	commonCodeCellStyle,
	commonNumberCellStyle,
	commonTextCellStyle,
} from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { RaonColDef } from "src/types/grid";
import {
	defaultColDef,
	getDefaultRowIndexColumnConfig,
	restoreGridScrollState,
	saveGridScrollState,
} from "src/utils/grid/gridStyleUtils";
import {
	createCellCssClassRules,
	GridCellConfig,
} from "src/utils/grid/GridHandlerContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { showWarning } from "src/utils/toast/toastUtil";
import {
	defaultKSK2040_R01DetailDto,
	KSK2040_R01DetailDto,
	KSK2040_R01InspectionValueField,
	KSK2040_R01_VALUE_FIELDS,
} from "./Dto_KSK2040_R01";
import { useKSK2040_R01Store } from "./Store_KSK2040_R01";
import {
	normalizeKSK2040_R01InspectionValue,
	normalizeKSK2040_R01ValueType,
	validateKSK2040_R01InspectionValue,
} from "./Util_KSK2040_R01";

const COLUMN_STATE_KEY = "KSK2040_R01-detail-grid-column-state";
const EDITABLE_COLUMNS = [...KSK2040_R01_VALUE_FIELDS, "remark"];
const HEADER_NAMES: Record<string, string> = {
	rowIndex: "",
	inspItemNm: "검사항목",
	specText: "기준값",
	valueTypeNm: "결과유형",
	inspectValue1_1: "검사값1",
	inspectValue1_2: "검사값2",
	inspectValue1_3: "검사값3",
	inspectValue1_4: "검사값4",
	inspectValue1_5: "검사값5",
	remark: "비고",
};

interface DetailTableKSK2040R01Props {
	detailGridRef: React.RefObject<AgGridReact<KSK2040_R01DetailDto> | null>;
}

interface PassFailCellRendererParams extends ICellRendererParams<KSK2040_R01DetailDto, string> {
	valueField: KSK2040_R01InspectionValueField;
	onChange: (
		valueIndex: number,
		field: KSK2040_R01InspectionValueField,
		value: "Y" | "N" | "",
	) => void;
}

const PassFailCellRenderer = (params: PassFailCellRendererParams) => {
	if (params.node.rowPinned) return "";
	if (!params.data || normalizeKSK2040_R01ValueType(params.data.valueType) !== "PASS_FAIL") {
		return String(params.value ?? "");
	}
	const current = params.data[params.valueField];
	const handleClick = (event: MouseEvent<HTMLButtonElement>, value: "Y" | "N") => {
		event.stopPropagation();
		params.onChange(
			params.data!.valueIndex,
			params.valueField,
			current === value ? "" : value,
		);
	};
	return (
		<div className="h-full w-full flex items-center justify-center gap-[3px] px-[2px]">
			<button
				type="button"
				className={`h-[34px] min-w-[34px] flex-1 rounded-[4px] border font-bold ${
					current === "Y" ? "bg-blue-dark text-white border-blue-dark" : "bg-white text-blue-dark border-blue-dark"
				}`}
				onClick={(event) => handleClick(event, "Y")}
			>Y</button>
			<button
				type="button"
				className={`h-[34px] min-w-[34px] flex-1 rounded-[4px] border font-bold ${
					current === "N" ? "bg-red-600 text-white border-red-600" : "bg-white text-red-600 border-red-600"
				}`}
				onClick={(event) => handleClick(event, "N")}
			>N</button>
		</div>
	);
};

const getSpecText = (params: ValueGetterParams<KSK2040_R01DetailDto>) => {
	if (!params.data) return "";
	return [
		params.data.specValue1,
		params.data.specRangeNm || params.data.specRange,
		params.data.specValue2,
		params.data.inspUnit,
	].filter(Boolean).join(" ");
};

export const DetailTable_KSK2040_R01 = ({ detailGridRef }: DetailTableKSK2040R01Props) => {
	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const {
		KSK2040_R01DetailList,
		updateKSK2040_R01DetailList,
		detailColumnState,
		setDetailColumnState,
		detailScrollTop,
		setDetailScrollTop,
	} = useKSK2040_R01Store();
	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
	} = useGridSettingContent<KSK2040_R01DetailDto>({
		gridRef: detailGridRef,
		key: "valueIndex",
		gridData: KSK2040_R01DetailList,
		defaultGridData: defaultKSK2040_R01DetailDto,
		selectedRow: undefined,
	});
	const { inspectionValueList } = useCommonCodeStore();
	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ["inspItemNm", "specText", "valueTypeNm"],
		insertedDisabledCells: [""],
		commonDisabledCells: [""],
		pointerCells: [...KSK2040_R01_VALUE_FIELDS, "remark"],
		insertedPointerCells: [""],
		commonPointerCells: [""],
		alignLeftCells: [""],
	}), []);
	const {
		disabledInsertedColumns,
		disabledColumns,
		pointerInsertedColumns,
		pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);
	const cellCssDefaultColDef = useMemo(() => ({
		...defaultColDef,
		cellClassRules: createCellClassRules<KSK2040_R01DetailDto>({
			idKey: "valueIndex",
			selectedList: [],
			deletedList: [],
			disabledInsertedColumns,
			disabledColumns,
			pointerInsertedColumns,
			pointerColumns,
			alignLeftColumns,
		}),
	}), [
		alignLeftColumns,
		createCellClassRules,
		disabledColumns,
		disabledInsertedColumns,
		pointerColumns,
		pointerInsertedColumns,
	]);
	const { handleCellKeyDown } = useDetailTableEvents<KSK2040_R01DetailDto>({
		detailList: KSK2040_R01DetailList,
		editableColumns: EDITABLE_COLUMNS,
		keyDownConfig: { enableArrowNavigation: true },
	});
	const valueTypeNameMap = useMemo(() => new Map(
		inspectionValueList.map((code) => [code.minorCd, code.codeNm]),
	), [inspectionValueList]);

	const updateValue = useCallback((
		valueIndex: number,
		field: KSK2040_R01InspectionValueField,
		value: string,
	) => {
		const row = KSK2040_R01DetailList.find((item) => item.valueIndex === valueIndex);
		if (!row) return;
		updateKSK2040_R01DetailList(valueIndex, {
			...row,
			[field]: normalizeKSK2040_R01InspectionValue(row.valueType, value),
			isModified: true,
		});
	}, [KSK2040_R01DetailList, updateKSK2040_R01DetailList]);

	const createValueColumn = useCallback((
		field: KSK2040_R01InspectionValueField,
		headerName: string,
	): RaonColDef<KSK2040_R01DetailDto> => ({
		headerName,
		colId: field,
		field,
		width: 110,
		editable: (params) => {
			const type = normalizeKSK2040_R01ValueType(params.data?.valueType ?? "");
			return !params.node.rowPinned && (type === "NUMERIC" || type === "TEXT");
		},
		pasteable: false,
		cellRenderer: PassFailCellRenderer,
		cellRendererParams: { valueField: field, onChange: updateValue },
		cellStyle: (params) => {
			const type = normalizeKSK2040_R01ValueType(params.data?.valueType ?? "");
			return type === "NUMERIC"
				? commonNumberCellStyle
				: type === "PASS_FAIL" ? commonCodeCellStyle : commonTextCellStyle;
		},
		valueParser: (params) => normalizeKSK2040_R01InspectionValue(
			params.data.valueType,
			params.newValue,
		),
	}), [updateValue]);

	const baseColumnDefs = useMemo<RaonColDef<KSK2040_R01DetailDto>[]>(() => [
		...getDefaultRowIndexColumnConfig(),
		{ headerName: "검사항목", colId: "inspItemNm", field: "inspItemNm", width: 160, editable: false },
		{ headerName: "기준값", colId: "specText", width: 200, editable: false, valueGetter: getSpecText },
		{
			headerName: "결과유형",
			colId: "valueTypeNm",
			field: "valueTypeNm",
			width: 100,
			cellStyle: (params) => params.node.rowPinned ? commonCodeCellStyle : undefined,
			valueFormatter: (params) => {
				const type = normalizeKSK2040_R01ValueType(params.data?.valueType ?? "");
				return valueTypeNameMap.get(type) || params.value || type;
			},
		},
		createValueColumn("inspectValue1_1", "검사값1"),
		createValueColumn("inspectValue1_2", "검사값2"),
		createValueColumn("inspectValue1_3", "검사값3"),
		createValueColumn("inspectValue1_4", "검사값4"),
		createValueColumn("inspectValue1_5", "검사값5"),
		{ headerName: "비고", colId: "remark", field: "remark", width: 180, editable: (params) => !params.node.rowPinned },
	], [createValueColumn, valueTypeNameMap]);

	const columnDefs = useMemo<RaonColDef<KSK2040_R01DetailDto>[]>(() =>
		GridCustomColumnDefs<KSK2040_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState?.some((state) => state.colId === "rowIndex")
				? detailColumnState
				: null,
			headerNames: HEADER_NAMES,
			fallbackHeaderClass: aggridHeaderClass,
		}) as RaonColDef<KSK2040_R01DetailDto>[], [baseColumnDefs, detailColumnState]);

	const pinnedBottomRowData = useMemo<KSK2040_R01DetailDto[]>(() => [{
		...defaultKSK2040_R01DetailDto,
		valueTypeNm: `${KSK2040_R01DetailList.length.toLocaleString()}건`,
	}], [KSK2040_R01DetailList.length]);

	const rowClassRules = useMemo(() =>
		createRowClassRules<KSK2040_R01DetailDto>({
			idKey: "valueIndex",
			selectedList: [],
			deletedList: [],
		}), [createRowClassRules]);

	const handleCellValueChanged = useCallback((event: CellValueChangedEvent<KSK2040_R01DetailDto>) => {
		if (!event.data || event.node.rowPinned || !event.colDef.field) return;
		const field = event.colDef.field;
		if (!KSK2040_R01_VALUE_FIELDS.includes(field as KSK2040_R01InspectionValueField)) {
			updateKSK2040_R01DetailList(event.data.valueIndex, {
				...event.data,
				isModified: true,
			});
			return;
		}

		const valueField = field as KSK2040_R01InspectionValueField;
		const normalizedValue = normalizeKSK2040_R01InspectionValue(
			event.data.valueType,
			event.newValue,
		);
		const updatedDetail = {
			...event.data,
			[valueField]: normalizedValue,
			isModified: true,
		};
		if (!validateKSK2040_R01InspectionValue(updatedDetail)) {
			const valueType = normalizeKSK2040_R01ValueType(event.data.valueType);
			showWarning(
				valueType === "NUMERIC"
					? "숫자 형식으로 입력해 주세요."
					: valueType === "TEXT"
						? "문자는 100자 이내로 입력해 주세요."
						: "검사 결과 형식을 확인해 주세요.",
			);
			const previousValue = String(event.oldValue ?? "");
			event.node.setDataValue(valueField, previousValue);
			updateKSK2040_R01DetailList(event.data.valueIndex, {
				...event.data,
				[valueField]: previousValue,
			});
			return;
		}

		updateKSK2040_R01DetailList(event.data.valueIndex, updatedDetail);
	}, [updateKSK2040_R01DetailList]);

	const handleCellClick = useCallback((event: CellClickedEvent<KSK2040_R01DetailDto>) => {
		if (!event.data || event.node.rowPinned) return;
		const field = event.colDef.field;
		if (field === "remark" || (
			KSK2040_R01_VALUE_FIELDS.includes(field as KSK2040_R01InspectionValueField)
			&& normalizeKSK2040_R01ValueType(event.data.valueType) !== "PASS_FAIL"
		)) event.api.startEditingCell({ rowIndex: event.rowIndex!, colKey: field! });
	}, []);

	const handleBodyScrollEnd = useCallback((event: BodyScrollEndEvent<KSK2040_R01DetailDto>) => {
		if (event.direction === "vertical") saveGridScrollState(event.api, setDetailScrollTop);
	}, [setDetailScrollTop]);

	const handleFirstDataRendered = useCallback((event: FirstDataRenderedEvent<KSK2040_R01DetailDto>) => {
		restoreGridScrollState(event.api, detailScrollTop);
	}, [detailScrollTop]);

	const initDetailSelection = useCallback(() => {
		detailGridRef.current?.api.deselectAll();
	}, [detailGridRef]);

	return (
		<GridContextWrapper<KSK2040_R01DetailDto>
			gridWrapperRef={gridWrapperRef}
			gridRef={detailGridRef}
			paddedGridData={KSK2040_R01DetailList}
			initSelected={initDetailSelection}
			defaultRowData={defaultKSK2040_R01DetailDto}
			baseColumnDefs={columnDefs}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			enableDataHistory={false}
		>
			<Grid_Excel<KSK2040_R01DetailDto>
				ref={detailGridRef}
				rowData={KSK2040_R01DetailList}
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}
				getRowId={getRowId}
				rowClassRules={rowClassRules}
				onCellClicked={handleCellClick}
				onCellValueChanged={handleCellValueChanged}
				onCellKeyDown={handleCellKeyDown}
				onBodyScrollEnd={handleBodyScrollEnd}
				onFirstDataRendered={handleFirstDataRendered}
				pinnedBottomRowData={pinnedBottomRowData}
				rowHeight={46}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
			/>
		</GridContextWrapper>
	);
};
